import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import type {
  VideoAsset,
  VideoMetadata,
} from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import VideoMetadataDialogFields from './VideoMetadataDialogFields';

interface VideoMetadataEditDialogProps {
  video: VideoAsset | null;
  onCancel: () => void;
  onSave: (externalId: string, fields: VideoMetadata) => Promise<void>;
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = Number(bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function formatDuration(seconds?: number): string {
  if (!seconds) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${mins}:${secs}`;
}

function usageLabel(count?: number): string {
  if (!count) return 'Not used yet';
  return `${count} place${count === 1 ? '' : 's'}`;
}

function adminInfoRows(video: VideoAsset): Array<[string, string]> {
  return [
    ['Uploaded by', video.createdByName || '—'],
    ['Uploaded', formatDate(video.createdAt)],
    ['Last updated', formatDate(video.updatedAt)],
    ['Original file', video.originalFilename || '—'],
    ['File size', formatBytes(video.fileSizeBytes)],
    ['Duration', formatDuration(video.durationSeconds)],
    ['Used in', usageLabel(video.referenceCount)],
  ];
}

function AdminInfo({ video }: { video: VideoAsset }) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        columnGap: 2,
        rowGap: 0.5,
      }}
    >
      {adminInfoRows(video).map(([label, value]) => (
        <React.Fragment key={label}>
          <Typography component="dt" variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography component="dd" variant="caption" sx={{ m: 0, wordBreak: 'break-word' }}>
            {value}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

/**
 * Edits a library video's metadata. Viewer-facing title and description are
 * mandatory and are what plays over the video; the library title is admin-only
 * and falls back to the viewer title when blank. The info block is read-only.
 */
export default function VideoMetadataEditDialog({
  video,
  onCancel,
  onSave,
}: VideoMetadataEditDialogProps) {
  const [publicTitle, setPublicTitle] = useState('');
  const [publicDescription, setPublicDescription] = useState('');
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!video) return;
    setPublicTitle(video.publicTitle ?? video.title);
    setPublicDescription(video.publicDescription ?? '');
    setTitle(video.title);
    setError(null);
  }, [video]);

  if (!video) return null;

  const canSave = publicTitle.trim().length > 0 && publicDescription.trim().length > 0 && !saving;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(video.externalId, {
        title: title.trim() || publicTitle.trim(),
        publicTitle: publicTitle.trim(),
        publicDescription: publicDescription.trim(),
      });
    } catch (err) {
      setError(
        (err as { error?: string; message?: string })?.error ??
          (err as Error)?.message ??
          'Could not save changes.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth aria-labelledby="edit-video-title">
      <DialogTitle id="edit-video-title">Edit video details</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <VideoMetadataDialogFields
          publicTitle={publicTitle}
          onPublicTitleChange={setPublicTitle}
          publicDescription={publicDescription}
          onPublicDescriptionChange={setPublicDescription}
          title={title}
          onTitleChange={setTitle}
        />
        <AdminInfo video={video} />

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSave} onClick={() => void save()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
